import {
  addSpend,
  Archived,
  Forbidden,
  InvalidInput,
  NotFound,
  uploadSpendBill,
  type SpendInput,
} from '@trippath/shared';

/**
 * Spends made without a connection, kept on the phone (IndexedDB) until they can be sent, each
 * with its photo. A spend's id is made on the phone, so one sent twice is kept once by the server.
 */
export interface Waiting {
  id: string;
  trip: string;
  input: SpendInput;
  photo: Blob | null;
  /** Why the server would not keep it (`no_rate`, `gone`): it waits for the person instead. */
  refused: string | null;
}

const NAME = 'trippath';
const STORE = 'outbox';

/** A request's result, or its error. */
function done<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => {
      resolve(request.result);
    };
    request.onerror = () => {
      reject(new Error('IndexedDB failed', { cause: request.error }));
    };
  });
}

function database(): Promise<IDBDatabase> {
  const opening = indexedDB.open(NAME, 1);
  opening.onupgradeneeded = () => {
    opening.result.createObjectStore(STORE, { keyPath: 'id' });
  };
  return done(opening);
}

async function store(mode: IDBTransactionMode): Promise<IDBObjectStore> {
  const db = await database();
  return db.transaction(STORE, mode).objectStore(STORE);
}

/** Keeps a spend to send later. */
export async function keep(trip: string, input: SpendInput, photo: Blob | null): Promise<void> {
  const waiting: Waiting = { id: input.id, trip, input, photo, refused: null };
  await done((await store('readwrite')).add(waiting));
}

/** What waits to be sent for a trip, the oldest first. */
export async function waiting(trip: string): Promise<Waiting[]> {
  const all = await done<unknown[]>((await store('readonly')).getAll());
  // Only this app writes the store, always a whole `Waiting`.
  return all.filter(
    (item): item is Waiting =>
      typeof item === 'object' && item !== null && 'trip' in item && item.trip === trip,
  );
}

/** Forgets a spend that waits: sent, or given up on. */
export async function drop(id: string): Promise<void> {
  await done((await store('readwrite')).delete(id));
}

async function refuse(item: Waiting, why: string): Promise<void> {
  await done((await store('readwrite')).put({ ...item, refused: why }));
}

/** Sends one spend and its photo: sent, refused by the server, or still no connection. */
async function sendOne(item: Waiting): Promise<'sent' | 'refused' | 'offline'> {
  try {
    const spend = await addSpend(item.trip, item.input);
    if (item.photo) await uploadSpendBill(item.trip, spend.id, item.photo);
    await drop(item.id);
    return 'sent';
  } catch (error: unknown) {
    if (error instanceof InvalidInput) {
      await refuse(item, error.fields.map((f) => f.problem).join(' ') || 'format');
    } else if (error instanceof NotFound || error instanceof Forbidden) {
      await refuse(item, 'gone');
    } else if (error instanceof Archived) {
      // Put away read only meanwhile: never taken, however often it is sent.
      await refuse(item, 'archived');
    } else {
      return 'offline';
    }
    return 'refused';
  }
}

let sending: Promise<number> | null = null;

/**
 * Sends what waits for a trip, in order, stopping at the first that cannot reach the server, and
 * answers how many were sent. Two calls at once share the one run.
 */
export function send(trip: string): Promise<number> {
  sending ??= (async () => {
    let sent = 0;
    try {
      for (const item of await waiting(trip)) {
        if (item.refused !== null) continue;
        const outcome = await sendOne(item);
        if (outcome === 'offline') break;
        if (outcome === 'sent') sent += 1;
      }
      return sent;
    } finally {
      sending = null;
    }
  })();
  return sending;
}
